import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { worksheetCategories, WorksheetCategory } from './data/worksheetsData';
import WorksheetCard from './components/WorksheetCard';
import SEOHead from '../../components/SEOHead';

const BADGE_COLORS: Record<string, string> = {
  Elementary: 'bg-pink-100 text-pink-700',
  'Middle School': 'bg-emerald-100 text-emerald-700',
  'High School': 'bg-sky-100 text-sky-700',
  'NY Regents': 'bg-rose-100 text-rose-700',
  'State Test Prep': 'bg-teal-100 text-teal-700',
  AP: 'bg-amber-100 text-amber-700',
  'Test Prep': 'bg-violet-100 text-violet-700',
};

const FILTER_TABS = [
  { id: 'all', label: 'All Levels' },
  { id: 'Elementary', label: 'Elementary' },
  { id: 'Middle School', label: 'Middle School' },
  { id: 'High School', label: 'High School' },
  { id: 'NY Regents', label: 'NY Regents' },
  { id: 'State Test Prep', label: 'State Tests' },
  { id: 'AP', label: 'AP' },
  { id: 'Test Prep', label: 'SAT / ACT' },
];

// Map category IDs to grade page routes and display names
const CATEGORY_TO_GRADE_MAP: Record<string, { route: string; label: string }> = {
  'kindergarten': { route: '/grades/kindergarten', label: 'Kindergarten' },
  'grade-1': { route: '/grades/grade-1', label: 'Grade 1' },
  'grade-2': { route: '/grades/grade-2', label: 'Grade 2' },
  'grade-3': { route: '/grades/grade-3', label: 'Grade 3' },
  'grade-4': { route: '/grades/grade-4', label: 'Grade 4' },
  'grade-5': { route: '/grades/grade-5', label: 'Grade 5' },
  'grade-6': { route: '/grades/grade-6', label: 'Grade 6' },
  'grade-7': { route: '/grades/grade-7', label: 'Grade 7' },
  'grade-8': { route: '/grades/grade-8', label: 'Grade 8' },
  'grade-9': { route: '/grades/grade-9', label: 'Grade 9 (Algebra I)' },
  'grade-10': { route: '/grades/grade-10', label: 'Grade 10 (Geometry)' },
  'grade-11': { route: '/grades/grade-11', label: 'Grade 11 (Algebra II)' },
  'grade-12': { route: '/grades/grade-12', label: 'Grade 12 (Precalculus)' },
};

export default function WorksheetsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [showMembershipBanner, setShowMembershipBanner] = useState(false);
  const membershipRef = useRef<HTMLDivElement>(null);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Printable Math Worksheets K-12 | Augustine Anyimadu-Ahenkae',
    description:
      'Download 3,000+ printable math worksheets for Kindergarten through Grade 12, AP Calculus, NY Regents, SAT, and ACT. Print-ready PDFs with answer keys aligned to Common Core and state standards.',
    url: `${siteUrl}/worksheets`,
    provider: {
      '@type': 'Person',
      name: 'Augustine Anyimadu-Ahenkae',
      url: siteUrl,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Worksheets', item: `${siteUrl}/worksheets` },
      ],
    },
  };

  // Auto-filter and expand category when arriving from a grade page
  useEffect(() => {
    if (categoryParam) {
      const matchedCategory = worksheetCategories.find(c => c.id === categoryParam);
      if (matchedCategory) {
        // Set filter to match the category's badge
        setActiveFilter(matchedCategory.badge);
        // Auto-expand this specific category
        setExpandedCategory(categoryParam);
        // Scroll to the category after a brief delay
        setTimeout(() => {
          const element = document.getElementById(`category-${categoryParam}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    }
  }, [categoryParam]);

  const filtered: WorksheetCategory[] =
    activeFilter === 'all'
      ? worksheetCategories
      : worksheetCategories.filter((c) => c.badge === activeFilter);

  const handleUnlock = () => {
    setShowMembershipBanner(true);
    setTimeout(() => {
      membershipRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const totalWorksheets = worksheetCategories.reduce((sum, c) => sum + c.worksheets.length, 0);
  const freeCount = worksheetCategories.reduce(
    (sum, c) => sum + c.worksheets.filter((w) => w.free).length,
    0
  );

  // Get breadcrumb info if arriving from a grade page
  const breadcrumbInfo = categoryParam ? CATEGORY_TO_GRADE_MAP[categoryParam] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      <SEOHead
        title="Printable Math Worksheets K-12 New York | Download PDF | Augustine Anyimadu-Ahenkae"
        description="Download 3,000+ printable math worksheets with answer keys. Kindergarten to Grade 12, AP Calculus, NY Regents, SAT, ACT — all aligned to Common Core and NY state standards. Print-ready PDFs."
        canonical="/worksheets"
        keywords="printable math worksheets, K-12 math worksheets, NY Regents worksheets, SAT math worksheets, AP Calculus worksheets, free math PDF worksheets"
        jsonLd={jsonLd}
      />
      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-700 hover:text-teal-600 transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            <span className="font-semibold text-sm">Back to Home</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-file-download-line text-2xl text-teal-600"></i>
            </div>
            <span className="font-bold text-gray-900 text-lg">Worksheets Library</span>
          </div>
          <button
            onClick={() => navigate('/#membership-tiers')}
            className="px-5 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Get Full Access
          </button>
        </div>
      </nav>

      {/* ── Breadcrumb (shown when arriving from a grade page) ── */}
      {breadcrumbInfo && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <button
              onClick={() => navigate(breadcrumbInfo.route)}
              className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm transition-colors duration-200 cursor-pointer group"
            >
              <i className="ri-arrow-left-s-line text-lg group-hover:-translate-x-1 transition-transform duration-200"></i>
              <span>Back to {breadcrumbInfo.label}</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-20 px-6">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/21ea51af9d5867c034e7cfbe291ec573.jpg"
            alt="Worksheets"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/85 via-teal-800/75 to-cyan-900/70"></div>
        </div>
        <div className="relative max-w-5xl mx-auto text-center text-white">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-5 py-2 rounded-full mb-6">
            <i className="ri-file-download-line text-teal-300"></i>
            <span className="text-sm font-semibold">Printable Practice Worksheets</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Downloadable Worksheets<br />
            <span className="text-teal-300">for Every Grade & Test</span>
          </h1>
          <p className="text-xl text-white/85 max-w-3xl mx-auto leading-relaxed mb-10">
            {totalWorksheets}+ carefully crafted worksheets covering Kindergarten through AP Calculus,
            NY Regents, SAT, ACT, and state tests. One free worksheet per topic — unlock everything with a membership.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center gap-2 bg-white/15 px-5 py-3 rounded-xl">
              <i className="ri-file-list-3-line text-teal-300 text-lg"></i>
              <span><strong>{totalWorksheets}+</strong> Total Worksheets</span>
            </div>
            <div className="flex items-center gap-2 bg-white/15 px-5 py-3 rounded-xl">
              <i className="ri-gift-line text-teal-300 text-lg"></i>
              <span><strong>{freeCount}</strong> Free Samples</span>
            </div>
            <div className="flex items-center gap-2 bg-white/15 px-5 py-3 rounded-xl">
              <i className="ri-graduation-cap-line text-teal-300 text-lg"></i>
              <span><strong>{worksheetCategories.length}</strong> Grade Levels & Courses</span>
            </div>
            <div className="flex items-center gap-2 bg-white/15 px-5 py-3 rounded-xl">
              <i className="ri-printer-line text-teal-300 text-lg"></i>
              <span>Print-Ready PDF Format</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Membership Upsell Banner (shown on unlock click) ── */}
      {showMembershipBanner && (
        <div ref={membershipRef} className="mx-6 mt-8 max-w-7xl lg:mx-auto">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl shrink-0">
                <i className="ri-lock-unlock-line text-3xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">Unlock All {totalWorksheets}+ Worksheets</h3>
                <p className="text-white/90 text-sm">
                  Members get instant access to every worksheet across all grades, Regents, SAT, ACT, and AP courses — plus video lessons, textbooks, and Q&amp;A support.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => setShowMembershipBanner(false)}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Dismiss
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-white text-amber-600 font-bold text-sm rounded-lg hover:bg-amber-50 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg"
              >
                View Membership Plans →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Filter Tabs ── */}
      <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeFilter === tab.id
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <main className="max-w-7xl mx-auto px-6 py-12">

        {/* Results count */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-gray-600 text-sm">
            Showing <strong>{filtered.length}</strong> {filtered.length === 1 ? 'category' : 'categories'}
            {activeFilter !== 'all' && ` in "${FILTER_TABS.find(t => t.id === activeFilter)?.label}"`}
          </p>
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-4 py-2 rounded-full border border-amber-200">
            <i className="ri-lock-line text-amber-500"></i>
            <span>Members unlock all worksheets</span>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-10">
          {filtered.map((category) => {
            const isExpanded = expandedCategory === category.id;
            const visibleWorksheets = isExpanded ? category.worksheets : category.worksheets.slice(0, 3);

            return (
              <div 
                key={category.id} 
                id={`category-${category.id}`}
                className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100"
              >
                {/* Category Header */}
                <div className={`bg-gradient-to-r ${category.gradient} p-6 text-white`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                        <i className={`${category.icon} text-2xl`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h2 className="text-xl font-bold">{category.label}</h2>
                          <span className={`px-3 py-0.5 text-xs font-bold rounded-full bg-white/25 text-white whitespace-nowrap`}>
                            {category.badge}
                          </span>
                        </div>
                        <p className="text-white/80 text-sm">
                          {category.worksheets.length} worksheets available &bull; {category.worksheets.filter(w => w.free).length} free
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="hidden md:flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg text-sm">
                        <i className="ri-lock-line text-white/80"></i>
                        <span className="text-white/90 font-medium">Full access with membership</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Worksheets Grid */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
                    {visibleWorksheets.map((worksheet) => (
                      <WorksheetCard
                        key={worksheet.id}
                        worksheet={worksheet}
                        gradient={category.gradient}
                        onUnlock={handleUnlock}
                      />
                    ))}
                  </div>

                  {/* Show more / Membership nudge */}
                  {category.worksheets.length > 3 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                        className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm transition-colors duration-200 cursor-pointer"
                      >
                        <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`}></i>
                        {isExpanded
                          ? 'Show less'
                          : `Show all ${category.worksheets.length} worksheets`}
                      </button>
                      <button
                        onClick={handleUnlock}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-md"
                      >
                        <i className="ri-lock-unlock-line"></i>
                        Unlock All {category.label} Worksheets
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Big Membership CTA ── */}
        <div className="mt-16 rounded-3xl overflow-hidden shadow-2xl">
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=Motivated%20student%20sitting%20at%20a%20bright%20clean%20desk%20surrounded%20by%20organized%20mathematics%20textbooks%20worksheets%20and%20a%20laptop%20showing%20online%20lessons%20warm%20encouraging%20academic%20atmosphere%20with%20soft%20natural%20light&width=1200&height=400&seq=ws-cta-001&orientation=landscape"
              alt="Unlock all worksheets"
              className="w-full h-72 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 via-teal-800/80 to-cyan-900/75"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-8">
              <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mb-5">
                <i className="ri-file-download-line text-4xl text-teal-300"></i>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Want Worksheets on <em>Every</em> Topic?
              </h2>
              <p className="text-xl text-white/85 max-w-2xl mb-8 leading-relaxed">
                A membership gives you instant, unlimited access to all {totalWorksheets}+ worksheets — plus 1,500+ video lessons, digital textbooks, and personalized Q&amp;A support from Mr. Augustine.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/')}
                  className="px-10 py-4 bg-gradient-to-r from-teal-400 to-cyan-500 text-white font-bold text-lg rounded-xl hover:opacity-90 transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Pick a Membership Plan
                </button>
                <button
                  onClick={() => navigate('/free-lessons')}
                  className="px-10 py-4 bg-white/20 border-2 border-white text-white font-bold text-lg rounded-xl hover:bg-white/30 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Try Free Lessons First
                </button>
              </div>
              <p className="mt-5 text-white/60 text-sm">Plans start at $19/month &bull; Cancel anytime &bull; 30-day guarantee</p>
            </div>
          </div>
        </div>

        {/* ── What's Included strip ── */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: 'ri-file-download-line', title: 'Print-Ready PDFs', desc: 'Every worksheet downloads as a clean, printer-friendly PDF — no formatting issues.', color: 'text-teal-600', bg: 'bg-teal-50' },
            { icon: 'ri-check-double-line', title: 'Answer Keys Included', desc: 'Every worksheet comes with a full answer key so students can self-check their work.', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { icon: 'ri-align-left', title: 'Standards-Aligned', desc: 'All worksheets align with Common Core, NY Regents, AP, SAT, and state standards.', color: 'text-amber-600', bg: 'bg-amber-50' },
          ].map((item, i) => (
            <div key={i} className={`${item.bg} rounded-2xl p-7 flex items-start gap-5`}>
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-white shadow-sm shrink-0`}>
                <i className={`${item.icon} text-2xl ${item.color}`}></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="mt-16 bg-gray-900 text-white py-10 px-6 text-center">
        <p className="text-gray-400 text-sm">
          © {new Date().getFullYear()} Augustine Anyimadu-Ahenkae Mathematics. All rights reserved.
        </p>
        <div className="flex justify-center gap-6 mt-4 text-sm">
          <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</button>
          <button onClick={() => navigate('/free-lessons')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">Free Lessons</button>
          <button onClick={() => navigate('/grades')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">All Grades</button>
        </div>
      </footer>
    </div>
  );
}